import { z } from 'zod';
import type { AssetTypeCode } from './constants';
import { SPLITTERS } from './constants';

/**
 * Per-type asset fields (PRD §5.3). One registry drives the form in the web app, the Zod
 * validation on both sides and the SQL for the `asset_*` detail tables in the API.
 */

export type DetailTable =
  'asset_network' | 'asset_olt' | 'asset_odc' | 'asset_odp' | 'asset_cpe' | 'asset_jc' | 'asset_pole';
export type FieldInput = 'text' | 'textarea' | 'number' | 'select' | 'ip' | 'tel';

/**
 * `technical`: set at creation by anyone who may create; changed later only by Admin/Planner.
 * `field`: technicians may also update it in their area (PRD §3.1 "update lapangan").
 */
export type EditLevel = 'technical' | 'field';

export interface FieldOption {
  value: string;
  label: string;
}

export interface DetailField {
  key: string;
  table: DetailTable;
  column: string;
  label: string;
  input: FieldInput;
  edit: EditLevel;
  section: string;
  options?: readonly FieldOption[];
  unit?: string;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  integer?: boolean;
  min?: number;
  max?: number;
  wide?: boolean;
  default?: string | number;
  /** Changing this value adds or removes ports. */
  drivesPorts?: boolean;
}

export type DetailValues = Record<string, string | number | null>;

const opts = (...pairs: Array<[string, string]>): FieldOption[] =>
  pairs.map(([value, label]) => ({ value, label }));
const ratios = (category: 'main' | 'odp') =>
  SPLITTERS.filter((s) => s.category === category).map((s) => ({ value: s.ratio, label: s.ratio }));

const port = (
  key: string,
  column: string,
  label: string,
  placeholder: string,
  edit: EditLevel = 'technical',
): DetailField => ({
  key,
  table: 'asset_network',
  column,
  label,
  input: 'number',
  integer: true,
  min: 1,
  max: 65535,
  placeholder,
  edit,
  section: 'Jaringan',
});

const ip = (edit: EditLevel): DetailField => ({
  key: 'ipAddress',
  table: 'asset_network',
  column: 'ip_address',
  label: 'Alamat IP',
  input: 'ip',
  placeholder: '10.10.0.1',
  hint: 'Dipakai untuk ping dan SNMP',
  edit,
  section: 'Jaringan',
});

export const ASSET_FIELDS: Record<AssetTypeCode, readonly DetailField[]> = {
  OLT: [
    {
      key: 'vendor',
      table: 'asset_olt',
      column: 'vendor',
      label: 'Vendor',
      input: 'select',
      edit: 'technical',
      section: 'Perangkat',
      options: opts(
        ['Huawei', 'Huawei'],
        ['ZTE', 'ZTE'],
        ['FiberHome', 'FiberHome'],
        ['VSOL', 'VSOL'],
        ['HSGQ', 'HSGQ'],
        ['C-Data', 'C-Data'],
        ['Lainnya', 'Lainnya'],
      ),
    },
    {
      key: 'ponPorts',
      table: 'asset_olt',
      column: 'pon_ports',
      label: 'Jumlah port PON',
      input: 'number',
      integer: true,
      min: 1,
      max: 128,
      default: 8,
      required: true,
      drivesPorts: true,
      edit: 'technical',
      section: 'Perangkat',
      hint: 'Port PON dibuat otomatis',
    },
    ip('technical'),
    port('httpPort', 'http_port', 'Port HTTP', '80'),
    port('httpsPort', 'https_port', 'Port HTTPS', '443'),
    port('mgmtPort', 'mgmt_port', 'Port SSH/Telnet', '22'),
  ],
  SERVER: [
    ip('technical'),
    port('httpPort', 'http_port', 'Port HTTP', '80'),
    port('httpsPort', 'https_port', 'Port HTTPS', '443'),
    port('mgmtPort', 'mgmt_port', 'Port Winbox/SSH', '8291'),
  ],
  AP: [
    ip('technical'),
    port('httpPort', 'http_port', 'Port HTTP', '80'),
    port('httpsPort', 'https_port', 'Port HTTPS', '443'),
  ],
  ODC: [
    {
      key: 'odcType',
      table: 'asset_odc',
      column: 'odc_type',
      label: 'Tipe ODC',
      input: 'select',
      default: 'pole',
      required: true,
      edit: 'technical',
      section: 'Kapasitas',
      options: opts(['pole', 'Pole mounted'], ['cabinet', 'Cabinet / ground'], ['wall', 'Dinding']),
    },
    {
      key: 'mainSplitter',
      table: 'asset_odc',
      column: 'main_splitter',
      label: 'Splitter utama',
      input: 'select',
      edit: 'technical',
      section: 'Kapasitas',
      options: ratios('main'),
    },
    {
      key: 'inputPorts',
      table: 'asset_odc',
      column: 'input_ports',
      label: 'Port input',
      input: 'number',
      integer: true,
      min: 1,
      max: 16,
      default: 1,
      required: true,
      drivesPorts: true,
      edit: 'technical',
      section: 'Kapasitas',
    },
    {
      key: 'outputPorts',
      table: 'asset_odc',
      column: 'output_ports',
      label: 'Port output (kapasitas)',
      input: 'number',
      integer: true,
      min: 1,
      max: 288,
      default: 16,
      required: true,
      drivesPorts: true,
      edit: 'technical',
      section: 'Kapasitas',
    },
    {
      key: 'odpSplitter',
      table: 'asset_odc',
      column: 'odp_splitter',
      label: 'Splitter ke ODP',
      input: 'select',
      edit: 'technical',
      section: 'Kapasitas',
      options: ratios('odp'),
    },
    {
      key: 'attenuationNotes',
      table: 'asset_odc',
      column: 'attenuation_notes',
      label: 'Catatan redaman',
      input: 'textarea',
      wide: true,
      edit: 'field',
      section: 'Pengukuran',
      placeholder: 'Hasil ukur OPM per port, tanggal ukur…',
    },
  ],
  ODP: [
    {
      key: 'odpType',
      table: 'asset_odp',
      column: 'odp_type',
      label: 'Tipe ODP',
      input: 'select',
      default: 'pole',
      required: true,
      edit: 'technical',
      section: 'Kapasitas',
      options: opts(['pole', 'Tiang'], ['wall', 'Dinding'], ['underground', 'Tanam / pedestal']),
    },
    {
      key: 'splitterRatio',
      table: 'asset_odp',
      column: 'splitter_ratio',
      label: 'Splitter',
      input: 'select',
      default: '1:8',
      required: true,
      edit: 'technical',
      section: 'Kapasitas',
      options: ratios('odp'),
    },
    {
      key: 'inputPorts',
      table: 'asset_odp',
      column: 'input_ports',
      label: 'Port input',
      input: 'number',
      integer: true,
      min: 1,
      max: 4,
      default: 1,
      required: true,
      drivesPorts: true,
      edit: 'technical',
      section: 'Kapasitas',
    },
    {
      key: 'outputPorts',
      table: 'asset_odp',
      column: 'output_ports',
      label: 'Port pelanggan (kapasitas)',
      input: 'number',
      integer: true,
      min: 1,
      max: 64,
      default: 8,
      required: true,
      drivesPorts: true,
      edit: 'technical',
      section: 'Kapasitas',
      hint: 'Biasanya sama dengan rasio splitter',
    },
    {
      key: 'cableLengthM',
      table: 'asset_odp',
      column: 'cable_length_m',
      label: 'Panjang kabel dari ODC',
      input: 'number',
      min: 0,
      max: 100000,
      unit: 'm',
      edit: 'field',
      section: 'Pengukuran',
    },
    {
      key: 'attenuationDb',
      table: 'asset_odp',
      column: 'attenuation_db',
      label: 'Redaman terukur',
      input: 'number',
      min: 0,
      max: 60,
      unit: 'dB',
      edit: 'field',
      section: 'Pengukuran',
    },
  ],
  CPE: [
    {
      key: 'customerName',
      table: 'asset_cpe',
      column: 'customer_name',
      label: 'Nama pelanggan',
      input: 'text',
      edit: 'field',
      section: 'Pelanggan',
    },
    {
      key: 'customerRef',
      table: 'asset_cpe',
      column: 'customer_ref',
      label: 'ID pelanggan',
      input: 'text',
      edit: 'field',
      section: 'Pelanggan',
      placeholder: 'Nomor di sistem billing',
    },
    {
      key: 'customerPhone',
      table: 'asset_cpe',
      column: 'customer_phone',
      label: 'Telepon',
      input: 'tel',
      edit: 'field',
      section: 'Pelanggan',
      placeholder: '08xx',
    },
    {
      key: 'servicePlan',
      table: 'asset_cpe',
      column: 'service_plan',
      label: 'Paket layanan',
      input: 'text',
      edit: 'field',
      section: 'Pelanggan',
      placeholder: 'Home 30 Mbps',
    },
    {
      key: 'customerAddress',
      table: 'asset_cpe',
      column: 'customer_address',
      label: 'Alamat pelanggan',
      input: 'textarea',
      wide: true,
      edit: 'field',
      section: 'Pelanggan',
    },
    {
      key: 'cpeKind',
      table: 'asset_cpe',
      column: 'cpe_kind',
      label: 'Jenis perangkat',
      input: 'select',
      default: 'ONT',
      required: true,
      edit: 'field',
      section: 'Perangkat',
      options: opts(['ONT', 'ONT'], ['HTB', 'HTB']),
    },
    {
      key: 'serialNumber',
      table: 'asset_cpe',
      column: 'serial_number',
      label: 'Serial number',
      input: 'text',
      edit: 'field',
      section: 'Perangkat',
      placeholder: 'ZTEGC1234567',
    },
    {
      key: 'installationType',
      table: 'asset_cpe',
      column: 'installation_type',
      label: 'Pemasangan',
      input: 'select',
      edit: 'field',
      section: 'Perangkat',
      options: opts(['indoor', 'Indoor'], ['outdoor', 'Outdoor'], ['wall', 'Dinding'], ['desktop', 'Meja']),
    },
    {
      key: 'connectionStatus',
      table: 'asset_cpe',
      column: 'connection_status',
      label: 'Status layanan',
      input: 'select',
      default: 'connected',
      required: true,
      edit: 'field',
      section: 'Perangkat',
      options: opts(
        ['connected', 'Terhubung'],
        ['disconnected', 'Terputus'],
        ['suspended', 'Diisolir'],
        ['maintenance', 'Maintenance'],
      ),
    },
    {
      key: 'bandwidthProfile',
      table: 'asset_cpe',
      column: 'bandwidth_profile',
      label: 'Profil bandwidth',
      input: 'text',
      edit: 'field',
      section: 'Perangkat',
    },
    ip('field'),
  ],
  JC: [
    {
      key: 'jcType',
      table: 'asset_jc',
      column: 'jc_type',
      label: 'Tipe closure',
      input: 'select',
      edit: 'technical',
      section: 'Kapasitas',
      options: opts(['dome', 'Dome / vertikal'], ['inline', 'Inline / horizontal']),
    },
    {
      key: 'spliceCapacity',
      table: 'asset_jc',
      column: 'splice_capacity',
      label: 'Kapasitas sambungan',
      input: 'number',
      integer: true,
      min: 1,
      max: 576,
      unit: 'core',
      edit: 'technical',
      section: 'Kapasitas',
    },
    {
      key: 'trayCount',
      table: 'asset_jc',
      column: 'tray_count',
      label: 'Jumlah tray',
      input: 'number',
      integer: true,
      min: 1,
      max: 48,
      edit: 'technical',
      section: 'Kapasitas',
    },
  ],
  POLE: [
    {
      key: 'heightM',
      table: 'asset_pole',
      column: 'height_m',
      label: 'Tinggi',
      input: 'number',
      min: 3,
      max: 30,
      unit: 'm',
      default: 7,
      edit: 'technical',
      section: 'Tiang',
    },
    {
      key: 'material',
      table: 'asset_pole',
      column: 'material',
      label: 'Material',
      input: 'select',
      default: 'beton',
      edit: 'technical',
      section: 'Tiang',
      options: opts(['beton', 'Beton'], ['besi', 'Besi'], ['kayu', 'Kayu'], ['lainnya', 'Lainnya']),
    },
    {
      key: 'owner',
      table: 'asset_pole',
      column: 'owner',
      label: 'Pemilik',
      input: 'select',
      default: 'own',
      edit: 'technical',
      section: 'Tiang',
      options: opts(
        ['own', 'Milik sendiri'],
        ['rent', 'Sewa'],
        ['pln', 'PLN'],
        ['telkom', 'Telkom'],
        ['other', 'Lainnya'],
      ),
    },
  ],
};

export function assetFields(type: string): readonly DetailField[] {
  return ASSET_FIELDS[type as AssetTypeCode] ?? [];
}

/** Default detail values for a new asset of this type. */
export function defaultDetails(type: string): DetailValues {
  return Object.fromEntries(assetFields(type).map((f) => [f.key, f.default ?? null]));
}

const trimBlank = (v: unknown) => {
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? null : t;
  }
  return v;
};

const toNumber = (v: unknown) => {
  const t = trimBlank(v);
  if (typeof t === 'string') {
    const n = Number(t.replace(',', '.'));
    return Number.isFinite(n) ? n : t;
  }
  return t;
};

function fieldSchema(f: DetailField): z.ZodTypeAny {
  const req = `${f.label} wajib diisi`;
  let base: z.ZodTypeAny;
  switch (f.input) {
    case 'number': {
      let n = z.number({ required_error: req, invalid_type_error: `${f.label} harus berupa angka` });
      if (f.integer) n = n.int(`${f.label} harus bilangan bulat`);
      if (f.min !== undefined) n = n.min(f.min, `${f.label} minimal ${f.min}`);
      if (f.max !== undefined) n = n.max(f.max, `${f.label} maksimal ${f.max}`);
      base = n;
      break;
    }
    case 'select': {
      const values = (f.options ?? []).map((o) => o.value) as [string, ...string[]];
      base = z.enum(values, {
        errorMap: (issue) => ({
          message:
            issue.code === 'invalid_type' && issue.received === 'undefined'
              ? req
              : `Pilih ${f.label.toLowerCase()} dari daftar`,
        }),
      });
      break;
    }
    case 'ip':
      base = z.string().ip({ message: 'Alamat IP tidak valid' });
      break;
    case 'tel':
      base = z.string().regex(/^[+0-9][0-9 ()-]{5,19}$/, 'Nomor telepon tidak valid');
      break;
    case 'textarea':
      base = z.string().max(2000, `${f.label} maksimal 2000 karakter`);
      break;
    default:
      base = z.string().max(200, `${f.label} maksimal 200 karakter`);
  }
  const pre = f.input === 'number' ? toNumber : trimBlank;
  if (f.default !== undefined) {
    // Blank falls back to the default, so these are never null in the database.
    return z.preprocess((v) => pre(v) ?? f.default, base);
  }
  if (f.required) return z.preprocess((v) => pre(v) ?? undefined, base);
  return z.preprocess((v) => pre(v) ?? null, base.nullable());
}

const detailsSchemaCache = new Map<string, z.ZodType<DetailValues>>();

/** Validates `details` for one asset type; unknown keys are dropped. */
export function assetDetailsSchema(type: string): z.ZodType<DetailValues> {
  let schema = detailsSchemaCache.get(type);
  if (!schema) {
    schema = z
      .object(Object.fromEntries(assetFields(type).map((f) => [f.key, fieldSchema(f)])))
      .strip() as unknown as z.ZodType<DetailValues>;
    detailsSchemaCache.set(type, schema);
  }
  return schema;
}

/** Common asset fields a technician may change after creation (plus `field`-level details). */
export const FIELD_LEVEL_COMMON_KEYS = ['status', 'description', 'address', 'lat', 'lng'] as const;

/** Keys of `details.*` a technician may change for this type. */
export function fieldLevelDetailKeys(type: string): string[] {
  return assetFields(type)
    .filter((f) => f.edit === 'field')
    .map((f) => f.key);
}

/** Technicians may nudge an asset by at most this distance (PRD §3.1). */
export const FIELD_MOVE_LIMIT_M = 50;
